import { Action, UserFlow } from '../../config/networkConfig';
import Mockable, { isMockable } from '../common/mockable';
import NetworkSettable, { isNetworkSettable } from '../common/networkSettable';
import RemoteModeSettable, { isRemoteModeSettable } from '../common/remoteModeSettable';
import CredentialReusable from '../common/credentialReusable';
import { Token } from '../common/token';
import HttpValidationService from './httpValidationService';
import MockableValidationService from './mockableValidationService';
import FaceScanValidationService from './localRemote/faceScanValidationService';
import {
  CreateValidationRequest,
  CredentialsResult,
  DeleteValidationResult,
  FileUploadRequest,
  GetStepRequest,
  ProcessStepRequest,
  StepResult,
  UploadResult,
  UserStatusRequest,
  UserStatusResult,
  ValidationRequest,
  ValidationResult,
  ValidationService,
  ValidationStep,
} from './validationService';
import CredentialReuseValidationService from './localRemote/credentialReuseValidationService';

export default class DynamicValidationService
  implements ValidationService, NetworkSettable, Mockable, RemoteModeSettable, CredentialReusable
{
  public validationService: ValidationService;

  private token?: Token;

  private testCase?: string;

  private remoteMode?: boolean;

  private reuseMode?: boolean;

  private action?: Action;

  constructor() {
    const isE2e =
      (process.env.REACT_APP_STAGE || 'unknown') === 'e2e' ||
      (process.env.REACT_APP_STAGE || 'unknown') === 'preprod_e2e';
    this.validationService = isE2e ? new MockableValidationService() : new HttpValidationService();
  }

  private switchToService(createService: () => ValidationService): void {
    this.validationService = createService();

    // configure the token, testCase and remote mode on the new instance
    if (this.token) {
      this.setToken(this.token);
    }

    if (this.testCase) {
      this.setMockMode(this.testCase);
    }

    if (this.remoteMode) {
      this.setRemoteMode(this.remoteMode);
    }

    if (this.reuseMode) {
      this.setCredentialReuseMode(this.reuseMode, this.action || Action.ISSUANCE);
    }
  }

  setMockMode(testCase: string): void {
    this.testCase = testCase;
    if (isMockable(this.validationService)) {
      this.validationService.setMockMode(testCase);
    }
  }

  setNetworkContext(flowType: string, action: Action): void {
    if (flowType === UserFlow.UNIQUENESS) {
      this.switchToService(() => new FaceScanValidationService(flowType, ValidationStep.uniqueness, action));
      return;
    }
    if (flowType === UserFlow.LIVENESS || flowType === UserFlow.FACE_AGE_CHECK_13) {
      this.switchToService(() => new FaceScanValidationService(flowType, ValidationStep.faceAuth, action));
      return;
    }

    if (flowType === UserFlow.DOCUMENT && action === Action.REFRESH) {
      this.switchToService(() => new FaceScanValidationService(flowType, ValidationStep.faceAuth, action));
      return;
    }

    if (isNetworkSettable(this.validationService)) {
      this.validationService.setNetworkContext(flowType, action);
    }
  }

  setCredentialReuseMode(reuseMode: boolean, action: Action): void {
    if (reuseMode === true) {
      this.switchToService(() => new CredentialReuseValidationService(action));
    }
  }

  setRemoteMode(remoteMode: boolean): void {
    this.remoteMode = remoteMode;
    if (isRemoteModeSettable(this.validationService)) {
      this.validationService.setRemoteMode(remoteMode);
    }
  }

  create(request: CreateValidationRequest): Promise<ValidationResult> {
    return this.validationService.create(request);
  }

  getNextStep(request: ValidationRequest): Promise<StepResult> {
    return this.validationService.getNextStep(request);
  }

  getStep(request: GetStepRequest): Promise<StepResult> {
    return this.validationService.getStep(request);
  }

  processStep(request: ProcessStepRequest): Promise<StepResult> {
    return this.validationService.processStep(request);
  }

  uploadFile(request: FileUploadRequest): Promise<UploadResult> {
    return this.validationService.uploadFile(request);
  }

  getValidationResult(request: ValidationRequest): Promise<ValidationResult> {
    return this.validationService.getValidationResult(request);
  }

  deleteValidation(request: ValidationRequest): Promise<DeleteValidationResult> {
    return this.validationService.deleteValidation(request);
  }

  getStatus(request: UserStatusRequest): Promise<UserStatusResult> {
    return this.validationService.getStatus(request);
  }

  getCredentials(request: ValidationRequest): Promise<CredentialsResult> {
    return this.validationService.getCredentials(request);
  }

  setToken(token: Token): void {
    this.token = token;
    return this.validationService.setToken(token);
  }
}
